import { debounce } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGetPartnersDocDeptDropdownsQuery } from "src/shared/api/dropdowns/partner";
import { useLazyGetPartnersSearchListQuery } from "src/shared/api/system";
import {
  EFilterType,
  IFilterCalendarItem,
  IFilterItem,
  IMultiSortItem,
  IMultiSortModel,
  TFilterPossibleValues,
} from "src/shared/components/Table/model/types";
import {
  parseSortParam,
  sortModelToArray,
} from "src/shared/lib/multi-sort/dataGridMultiSort";
import { INameValue } from "src/shared/types/common.types";
import { IBaseQueryParams } from "src/shared/types/query";
import { sanitizeQueryParams } from "src/shared/utils/url/query.helpers";
import { useTableSearchParams } from "../useTableSearchParams";

type IBaseQueryParamsWithoutSort = Omit<IBaseQueryParams, "sort">;

export interface IGetPartnersDocDeptCommonQuery
  extends IBaseQueryParamsWithoutSort {
  document_status?: string[];
  agencies?: string[];
  partners?: string[];
  partner_name?: string[];
  document_types?: string[];
  periods?: string[];
  signing_methods?: string[];
  document_statuses?: string[];
  contract_statuses?: string[];
  sort?: string | string[];
}

interface IUseGetPartnersDocDeptFilters {
  partnerName?: string;
}

export interface IPartnersDocDebtParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: string;
}

const SORT_OPTIONS = [
  { name: "Партнёр", value: "partner" },
  { name: "Компания", value: "agency" },
  { name: "КД-А Партнера", value: "contract" },
  { name: "Документ + Способ подписания", value: "document_type" },
  { name: "Дата документа", value: "document_date" },
  { name: "Статус документа", value: "doc_status" },
  { name: "Срок задолженности", value: "debt_days" },
  { name: "Статус КДА", value: "contract_status" },
];

function normalizeMulti(value: unknown) {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === "") return undefined;

  return [value];
}

function mergeUniquePartners(
  prev: Array<INameValue<number>>,
  next: Array<INameValue<number>>,
) {
  const map = new Map<number, INameValue<number>>();

  prev.forEach((item) => map.set(item.value, item));
  next.forEach((item) => map.set(item.value, item));

  return Array.from(map.values());
}

export function useGetPartnersDocDeptFilters({
  partnerName,
}: IUseGetPartnersDocDeptFilters = {}) {
  const [partnersList, setPartnersList] = useState<Array<INameValue<number>>>(
    [],
  );
  const partnersPrefetchDone = useRef(false);

  const {
    paramsObject,
    onPaginationModelChange,
    onSearch,
    onResetFilter,
    onFilter,
  } = useTableSearchParams<IGetPartnersDocDeptCommonQuery>({});

  const {
    per_page,
    page,
    search,
    agencies,
    partners,
    partner_name,
    document_types,
    signing_methods,
    document_statuses,
    contract_statuses,
    periods,
    sort,
  } = paramsObject;

  const sortModel: GridSortModel = useMemo(() => parseSortParam(sort), [sort]);

  // TODO: remove if this one willnot be needed
  // const onSortModelChange = useCallback(
  //   (model: GridSortModel) => {
  //     const nextArr = model
  //       .filter((i) => i.sort)
  //       .map((i) => `${i.field}:${i.sort}`);

  //     onFilter([{ name: "sort", value: nextArr }]);
  //   },
  //   [onFilter],
  // );

  const multiSortValue: IMultiSortItem[] = useMemo(() => {
    return sortModel
      .filter((item) => item.sort)
      .map((item) => ({
        sortBy: item.field,
        sortDirection: item.sort as "asc" | "desc",
      }));
  }, [sortModel]);

  const writeSortToUrl = useCallback(
    (pairs: string[]) => {
      onFilter([{ name: "sort", value: pairs }]);
    },
    [onFilter],
  );

  const onMultiSortChange = useCallback(
    (next: IMultiSortItem[]) => {
      writeSortToUrl(next.map((i) => `${i.sortBy}:${i.sortDirection}`));
    },
    [writeSortToUrl],
  );

  const tableSortModel: IMultiSortModel = useMemo(
    () => ({
      mode: "multi",
      options: SORT_OPTIONS,
      value: multiSortValue,
      onChange: onMultiSortChange,
    }),
    [multiSortValue, onMultiSortChange],
  );

  const sortObject = useMemo(() => sortModelToArray(sortModel), [sortModel]);

  const rawQuery = {
    search,
    per_page: per_page ? Number(per_page) : undefined,
    page: page ? Number(page) : 1,
    sort: sortObject,
    agencies: normalizeMulti(agencies),
    partners: normalizeMulti(partners),
    partner_name: normalizeMulti(partner_name), // <-- добавили
    document_types: normalizeMulti(document_types),
    periods: normalizeMulti(periods),
    signing_methods: normalizeMulti(signing_methods),
    document_statuses: normalizeMulti(document_statuses),
    contract_statuses: normalizeMulti(contract_statuses),
  };

  const query = sanitizeQueryParams(rawQuery);

  const { data: dropdowns } = useGetPartnersDocDeptDropdownsQuery(undefined);
  const [searchPartners, { isFetching: isPartnersFetching }] =
    useLazyGetPartnersSearchListQuery();

  const loadPartners = useCallback(
    (value: string) => {
      if (value.trim().length < 3) return;
      searchPartners({ name: value })
        .unwrap()
        .then((resp) => {
          setPartnersList((prev) => mergeUniquePartners(prev, resp));
        })
        .catch(() => {
          enqueueSnackbar("Не удалось загрузить опции партнеров", {
            variant: "error",
            handleClose: closeSnackbar,
          });
        });
    },
    [searchPartners],
  );

  const debouncedLoadPartners = useMemo(
    () => debounce(loadPartners, 500),
    [loadPartners],
  );

  useEffect(() => {
    if (!partners?.length || partnersPrefetchDone.current || !partnerName)
      return;
    searchPartners({ name: partnerName })
      .unwrap()
      .then((resp) => {
        setPartnersList((prev) => mergeUniquePartners(prev, resp));
        partnersPrefetchDone.current = true;
      });
  }, [partners, partnerName, searchPartners]);

  useEffect(() => {
    const selectedPartnerIds = Array.isArray(partners)
      ? partners.map(Number)
      : partners
        ? [Number(partners)]
        : [];

    if (!selectedPartnerIds.length) return;

    const selectedOptions = partnersList.filter((item) =>
      selectedPartnerIds.includes(item.value),
    );

    if (!selectedOptions.length) return;

    const nextPartnerNames = selectedOptions.map((item) => item.name);
    const currentPartnerNames = Array.isArray(partner_name)
      ? partner_name
      : partner_name
        ? [partner_name]
        : [];

    const isEqual =
      currentPartnerNames.length === nextPartnerNames.length &&
      currentPartnerNames.every(
        (name, index) => name === nextPartnerNames[index],
      );

    if (isEqual) return;

    onFilter([{ name: "partner_name", value: nextPartnerNames }]);
  }, [partners, partner_name, partnersList, onFilter]);

  useEffect(() => {
    const selectedPartnerIds = Array.isArray(partners)
      ? partners.map(Number)
      : partners
        ? [Number(partners)]
        : [];

    if (!selectedPartnerIds.length) return;

    const allSelectedLoaded = selectedPartnerIds.every((id) =>
      partnersList.some((item) => item.value === id),
    );

    if (allSelectedLoaded) return;

    const partnerNames = Array.isArray(partner_name)
      ? partner_name
      : partner_name
        ? [partner_name]
        : [];

    if (!partnerNames.length) return;

    partnerNames.forEach((name) => {
      loadPartners(name);
    });
  }, [partners, partner_name, partnersList, loadPartners]);

  useEffect(() => {
    const hasPartners = Array.isArray(partners)
      ? partners.length > 0
      : Boolean(partners);

    const hasPartnerNames = Array.isArray(partner_name)
      ? partner_name.length > 0
      : Boolean(partner_name);

    if (!hasPartners && hasPartnerNames) {
      onFilter([{ name: "partner_name", value: null }]);
    }
  }, [partners, partner_name, onFilter]);

  const filters: (IFilterItem<TFilterPossibleValues> | IFilterCalendarItem)[] =
    useMemo(
      () => [
        {
          name: "partners",
          loading: isPartnersFetching,
          type: EFilterType.MULTI_AUTOCOMPLETE,
          value: Array.isArray(partners)
            ? partners
            : partners
              ? [partners]
              : [],
          title: "Партнер",
          options: partnersList || [],
          onInputChange: (_e, value, reason) => {
            if (reason !== "input") return;
            debouncedLoadPartners(value);
          },
        },
        {
          name: "agencies",
          type: EFilterType.MULTI_SELECT,
          title: "Компания",
          loading: isPartnersFetching,
          value: Array.isArray(agencies)
            ? agencies.map(Number)
            : agencies
              ? [Number(agencies)]
              : [],
          options: dropdowns?.agencies || [],
        },
        {
          name: "document_types",
          type: EFilterType.MULTI_SELECT,
          title: "Тип документ",
          value: Array.isArray(document_types)
            ? document_types
            : document_types
              ? [document_types]
              : [],
          options: dropdowns?.document_types || [],
        },
        {
          name: "contract_statuses",
          type: EFilterType.MULTI_SELECT,
          title: "Статус КДА",
          value: Array.isArray(contract_statuses)
            ? contract_statuses.map(Number)
            : contract_statuses
              ? [Number(contract_statuses)]
              : [],
          options: dropdowns?.contract_statuses || [],
        },
        {
          name: "document_statuses",
          type: EFilterType.MULTI_SELECT,
          title: "Статус документа",
          value: Array.isArray(document_statuses)
            ? document_statuses
            : document_statuses
              ? [document_statuses]
              : [],
          options: dropdowns?.document_statuses || [],
        },
        {
          name: "signing_methods",
          type: EFilterType.MULTI_SELECT,
          title: "Способ подписания",
          value: Array.isArray(signing_methods)
            ? signing_methods
            : signing_methods
              ? [signing_methods]
              : [],
          options: dropdowns?.signing_methods || [],
        },
        {
          name: "periods",
          type: EFilterType.MULTI_SELECT,
          title: "Срок задолженности",
          value: Array.isArray(periods)
            ? periods.map(Number)
            : periods
              ? [Number(periods)]
              : [],
          options: dropdowns?.periods || [],
        },
      ],
      [
        isPartnersFetching,
        partners,
        partnersList,
        agencies,
        document_types,
        contract_statuses,
        document_statuses,
        signing_methods,
        periods,
        dropdowns?.agencies,
        dropdowns?.document_types,
        dropdowns?.contract_statuses,
        dropdowns?.document_statuses,
        dropdowns?.signing_methods,
        dropdowns?.periods,
        debouncedLoadPartners,
      ],
    );

  return {
    query,
    filters,
    tableSortModel,
    onPaginationModelChange,
    onSearch,
    onResetFilter,
    onFilter,
  };
}
